import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Button, Input, Modal } from '@/components/ui'
import { waProxy } from '@/lib/whatsappProxy'
import { formatWhatsAppPhone } from '@/utils/phone'

interface SendMessageModalProps {
  orgId: string
  isOpen: boolean
  onClose: () => void
}

interface SendMessageResponse {
  id?: string
  messageId?: string
}

export default function SendMessageModal({ orgId, isOpen, onClose }: SendMessageModalProps) {
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setPhone('')
      setMessage('')
      setIsSending(false)
    }
  }, [isOpen])

  const submit = async () => {
    if (!phone.trim() || !message.trim()) {
      toast.error('Phone and message are required.')
      return
    }

    setIsSending(true)
    try {
      const result = await waProxy<SendMessageResponse>('/api/messages/send', 'POST', {
        orgId,
        to: formatWhatsAppPhone(phone),
        type: 'text',
        message: message.trim(),
      })
      const messageId = result?.messageId ?? result?.id ?? 'unknown'
      toast.success(`✅ Sent! Message ID: ${messageId}`)
      onClose()
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : 'Unknown error'
      toast.error(`❌ Failed: ${errMsg}`)
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Send Test Message (${orgId})`}
      size="md"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={isSending}>
            Send
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <Input
          label="To"
          placeholder="0528393969"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <div>
          <label className="block text-sm font-medium text-ink-secondary mb-1.5">Message</label>
          <textarea
            rows={4}
            className="w-full px-3 py-2 rounded border border-navy-border bg-navy-deeper text-ink-primary placeholder:text-ink-muted transition-colors focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold/60"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter message text..."
          />
        </div>
      </div>
    </Modal>
  )
}
